import { inject, Injectable } from '@angular/core';
import { CmsApiService } from '../../core/services/api/cms-api.service';
import { CmsContentMapperService } from '../../core/services/cms-content-mapper.service';
import { from, mergeMap, Observable, ReplaySubject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PlatformService, TimeService } from 'ngx-unificator/services';
import { ContentUpdaterService } from '../../core/services/content-updater/content-updater.service';
import { BlogCategoryEnum } from '../../core/enums/blog-category.enum';
import { ReloadBonusType } from '../../core/types/bonuses.type';
import { getDayNamesKeys } from '../../core/helpers/day-names';
import { NotificationCenterService } from '../../core/shared/layout/notification-center/notification-center.service';

@Injectable({
  providedIn: 'root',
})
export class BlogService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _sanitizer = inject(DomSanitizer);
  private _time = inject(TimeService);
  private _contentUpdater = inject(ContentUpdaterService);
  private _platform = inject(PlatformService);
  private _notificationCenter = inject(NotificationCenterService);
  private _blogBonuses$ = new ReplaySubject<any[]>(1);

  /**
   * Get news list from CMS and prepare for using in frontend
   *
   * @param params
   */
  list(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('news/list', params, () =>
      this._api.newsList(params).pipe(
        map((response) =>
          this._mapArticleList(
            this._mapper.mapCmsData(response.data.list, {
              id: 'id',
              slug: 'alias',
              type: 'type',
              publishAt: 'publishAt.date',
              unpublishAt: 'unpublishAt.date',
              countTotal: { value: response.data?.countTotal },
            }),
          ),
        ),
      ),
    );
  }

  /**
   * Get news item from CMS and prepare for using in frontend
   *
   * @param params
   */
  item(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('news/item', params, () =>
      this._api.newsItem(params).pipe(
        map((response) => {
          if (response.statusCode === 200) {
            return this._mapArticleList(
              this._mapper.mapCmsData(response.data, {
                id: 'id',
                slug: 'alias',
                type: 'type',
                publishAt: 'publishAt.date',
                unpublishAt: 'unpublishAt.date',
              }),
            );
          } else {
            return [
              {
                statusCode: response.statusCode,
              },
            ];
          }
        }),
      ),
    );
  }

  public getBlogNotifications() {
    if (this._platform.isBrowser) {
      this.list({
        type: BlogCategoryEnum.NOTIFICATIONS,
      })
        .pipe(
          filter((data) => !!data),
          mergeMap((list: any[]) => from(list)),
          mergeMap((notif) => from(this._notificationCenter.addNotifToStore(notif))),
        )
        .subscribe();
    }
  }

  public getBlogBonuses() {
    if (this._platform.isBrowser) {
      this._notificationCenter.clearExpiredNotifications();

      this.list({
        type: BlogCategoryEnum.PROMOTIONS,
      })
        .pipe(
          filter((data) => !!data),
          tap((list: any[]) => this._blogBonuses$.next(list)),
          map((list) => list.map((item) => this._enrichBonusItem(item)).filter((item) => this._shouldShowBonus(item, list))),
          mergeMap((list) => from(list)),
          mergeMap((notif) => from(this._notificationCenter.addNotifToStore(notif))),
        )
        .subscribe();
    }
  }

  public blogBonuses$(): Observable<any[]> {
    return this._blogBonuses$.asObservable();
  }

  /**
   * Prepare article list for using in frontend
   *
   * @param list
   * @private
   */
  private _mapArticleList(list: any[]): any[] {
    return list.map((article) => ({
      ...article,
      video: this._prepareVideoSrc(article.Video),
      published: this._getDateLabel(article.publishAt),
    }));
  }

  /**
   * Returns correct video src by provided Vimeo link
   *
   * @param src
   * @private
   */
  private _prepareVideoSrc(src: string): SafeResourceUrl {
    if (!src) {
      return;
    }

    const videoId = src.split('/').pop();

    return this._sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`);
  }

  /**
   * Returns correct date label by provided date string
   *
   * @param date
   * @private
   */
  private _getDateLabel(date: string): { num: number | string; label: string; date: Date } {
    const daysCount = this._time.timeDiff(this._time.toLocalDate(date)).days;
    const label = daysCount > 6 ? this._time.toLocalDate(date).toString() : daysCount === 0 ? 't.today' : daysCount === 1 ? 't.yesterday' : 't.days-ago';

    return {
      num: daysCount > 1 ? daysCount : '',
      label,
      date: this._time.toLocalDate(date),
    };
  }

  private _enrichBonusItem(item: ReloadBonusType) {
    const day = new Date().getUTCDay();
    const dayNames = getDayNamesKeys();
    const parsedDays = item?.AllowedDays?.split(',').map(Number) ?? [];

    const nearestBonusDay = this._getNearestBonusDay(parsedDays, day, dayNames);

    return {
      ...item,
      nearestBonusDay,
      isToday: parsedDays.includes(day),
    };
  }

  private _getNearestBonusDay(parsedDays: number[], today: number, dayNames: Record<number, string>): string {
    const next = parsedDays.map((d) => ({ day: d, diff: (d - today + 7) % 7 })).sort((a, b) => a.diff - b.diff)[0];

    return dayNames[next.day];
  }

  private _shouldShowBonus(item: any, bonusList: any[]): boolean {
    if (!item.isToday || !item.isShowOnDepositPage) {
      return false;
    }

    const bonusBoId = item?.BonusBOId?.trim();

    const hasBonusToday =
      !!bonusBoId &&
      bonusList?.some((bonus) => bonus?.name?.trim().toUpperCase().includes(bonusBoId.toUpperCase()) && this._time.isTodayDate(new Date(bonus?.createdAt)));

    return !hasBonusToday;
  }
}
